import { type FC } from "react";
import {
  Card,
  Box,
  Checkbox,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import {
  useContentStatusChangedSubscription,
  type ContentsItem,
} from "@/partner/entities/contents";
import { useDeviceBreakpoint } from "@/partner/shared/hooks";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";
import { PremiumBadge } from "@/partner/shared/ui";
import { ContentProcessingStatusType } from "@/types/graphql/partner.generated";

const TITLE_MAX_LENGTH = 200;

// Как в референсе (partner/components/Thumbnail/Thumbnail.js): пока нет
// файла/идёт обработка/ошибка — миниатюра не пытается показать картинку,
// а целиком превращается в тёмную плашку с подписью (без спиннера). COMPLETE
// помечен null явно — для него миниатюра рендерит превью-картинку (см. ветку
// else ниже), а не текстовую плашку; полный Record гарантирует, что новый
// статус в enum не провалится в превью молча, а потребует решения здесь.
const CONTENT_STATUS_LABEL_KEYS: Record<
  ContentProcessingStatusType,
  string | null
> = {
  [ContentProcessingStatusType.NofileType]: "videos.status.nofile",
  [ContentProcessingStatusType.ProcessingType]: "videos.status.processing",
  [ContentProcessingStatusType.ErrorType]: "videos.status.error",
  [ContentProcessingStatusType.CompleteType]: null,
};

function truncate(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

function formatDuration(seconds?: number | null): string | null {
  if (!seconds) return null;
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, "0");
  const ss = String(sec).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

interface VideoListItemProps {
  content: ContentsItem;
  selected: boolean;
  onSelectOneChange: (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onClickItem: (id: string) => void;
}

export const VideoListItem: FC<VideoListItemProps> = ({
  content,
  selected,
  onSelectOneChange,
  onClickItem,
}) => {
  const { t } = useTranslation();
  // Живое обновление статуса, пока видео обрабатывается
  useContentStatusChangedSubscription({
    variables: { id: content.id },
    skip: content.processingStatus !== "PROCESSING",
  });

  const isMobile = useDeviceBreakpoint("md");
  const statusLabelKey = CONTENT_STATUS_LABEL_KEYS[content.processingStatus];
  const publishAt = content.publishAt ? new Date(content.publishAt) : null;
  const duration = formatDuration(content.duration);

  return (
    <Card
      elevation={0}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 2,
        px: 1,
        cursor: "pointer",
        opacity: content.publishAt ? 1 : 0.6,
        bgcolor: selected ? "background.selected" : "background.paper",
      }}
      onClick={() => onClickItem(content.id)}
    >
      <Checkbox
        checked={selected}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onSelectOneChange(content.id, e)}
      />

      <Box
        sx={{
          position: "relative",
          width: { xs: 160 / 1.5, sm: 160 },
          height: { xs: 90 / 1.5, sm: 90 },
          flexShrink: 0,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "action.hover",
        }}
      >
        {statusLabelKey ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              p: 1,
              bgcolor: "rgba(0,0,0,0.85)",
              color: "#fff",
            }}
          >
            <Typography variant="caption">{t(statusLabelKey)}</Typography>
          </Box>
        ) : (
          <>
            {(content.preview?.url || content.playback?.thumbnailUrl) && (
              <img
                src={String(
                  content.preview?.url ?? content.playback?.thumbnailUrl
                )}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            )}
            {content.premium && (
              <PremiumBadge title={t("videos.item.premiumTooltip")} />
            )}
            {duration && (
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  right: 4,
                  bottom: 4,
                  px: 0.5,
                  borderRadius: 0.5,
                  bgcolor: "rgba(0,0,0,0.7)",
                  color: "#fff",
                }}
              >
                {duration}
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <ItemTitleLink
          to={`${PagePaths.videos}?tab=edit&id=${content.id}`}
          onOpen={() => onClickItem(content.id)}
          underline="hover"
          variant="h6"
          color="text.primary"
          sx={{
            display: "block",
            fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.2rem" },
          }}
        >
          {truncate(content.title, TITLE_MAX_LENGTH)}
        </ItemTitleLink>
        {content.tags.length > 0 && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ mt: 0.5, flexWrap: "wrap" }}
          >
            {content.tags.map((tag) => (
              <Chip
                key={tag.id}
                size="small"
                variant="outlined"
                label={tag.title}
              />
            ))}
          </Stack>
        )}
      </Box>

      {!isMobile && (
        <Box sx={{ flexShrink: 0, minWidth: 130 }}>
          <Typography variant="body2" color="text.primary">
            {t("videos.item.viewersOnline", { count: content.viewersOnline })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("videos.item.viewsCount", { count: content.totalViews })}
          </Typography>
        </Box>
      )}

      {!isMobile && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            justifyContent: "flex-end",
          }}
        >
          {publishAt ? (
            <>
              <Box>
                <Typography variant="body2" color="text.primary" align="right">
                  {t("videos.item.publishedAtTime", {
                    publishAt: content.publishAt,
                  })}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  align="right"
                >
                  {t("videos.item.publishedAtDate", {
                    publishAt: content.publishAt,
                  })}
                </Typography>
              </Box>

              {content.url && (
                <Tooltip title={t("videos.item.openTooltip")}>
                  <IconButton
                    target="_blank"
                    href={content.url}
                    size="large"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <OpenInNewIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.primary" align="right">
              {t("videos.item.notPublished")}
            </Typography>
          )}
        </Box>
      )}
    </Card>
  );
};
